// Search service for site-wide search functionality

// Mock data for search results
const mockSearchData = {
  sports: [
    { id: 1, type: 'sport', title: 'Basketball', description: 'Boys and Girls Basketball teams', url: '/sports/basketball', category: 'Sports' },
    { id: 2, type: 'sport', title: 'Football', description: 'Varsity Football team', url: '/sports/football', category: 'Sports' },
    { id: 3, type: 'sport', title: 'Soccer', description: 'Boys and Girls Soccer teams', url: '/sports/soccer', category: 'Sports' },
    { id: 4, type: 'sport', title: 'Baseball', description: 'Varsity Baseball team', url: '/sports/baseball', category: 'Sports' },
    { id: 5, type: 'sport', title: 'Softball', description: 'Varsity Softball team', url: '/sports/softball', category: 'Sports' },
    { id: 6, type: 'sport', title: 'Track & Field', description: 'Boys and Girls Track & Field', url: '/sports/track-field', category: 'Sports' },
  ],
  news: [
    { id: 1, type: 'news', title: 'Basketball Season Kicks Off', description: 'Eagles ready for another championship run', url: '/news/basketball-season-kicks-off', category: 'News', date: '2024-01-15' },
    { id: 2, type: 'news', title: 'New Athletic Director Announced', description: 'Welcome our new athletic leadership', url: '/news/new-athletic-director', category: 'News', date: '2024-01-10' },
    { id: 3, type: 'news', title: 'Football Team Wins Regional Championship', description: 'Historic victory for the Eagles', url: '/news/football-regional-championship', category: 'News', date: '2024-01-05' },
  ],
  events: [
    { id: 1, type: 'event', title: 'Basketball vs. Central High', description: 'Home game at 7:00 PM', url: '/events/basketball-vs-central', category: 'Events', date: '2024-02-15' },
    { id: 2, type: 'event', title: 'Athletic Booster Club Meeting', description: 'Monthly meeting in the library', url: '/events/booster-club-meeting', category: 'Events', date: '2024-02-10' },
    { id: 3, type: 'event', title: 'Spring Sports Registration', description: 'Register for spring sports', url: '/events/spring-registration', category: 'Events', date: '2024-02-20' },
  ],
  pages: [
    { id: 1, type: 'page', title: 'Family Resources', description: 'Important information for families', url: '/family-resources', category: 'Resources' },
    { id: 2, type: 'page', title: 'Athletic Handbook', description: 'Complete athletic handbook', url: '/family-resources/handbook', category: 'Resources' },
    { id: 3, type: 'page', title: 'Fees and Forms', description: 'Athletic fees and required forms', url: '/family-resources/fees', category: 'Resources' },
    { id: 4, type: 'page', title: 'Safety Protocols', description: 'Health and safety information', url: '/safety', category: 'Resources' },
    { id: 5, type: 'page', title: 'Facilities', description: 'Athletic facilities information', url: '/facilities', category: 'Resources' },
    { id: 6, type: 'page', title: 'Coach Resources', description: 'Resources for coaches', url: '/coach-resources', category: 'Resources' },
    { id: 7, type: 'page', title: 'Donations', description: 'Support our athletic programs', url: '/donations', category: 'Support' },
    { id: 8, type: 'page', title: 'Media Gallery', description: 'Photos and videos from games', url: '/media', category: 'Media' },
  ]
};

// Search categories for filtering
export const searchCategories = [
  { id: 'all', name: 'All Results', icon: 'MagnifyingGlassIcon' },
  { id: 'sports', name: 'Sports', icon: 'TrophyIcon' },
  { id: 'news', name: 'News', icon: 'DocumentTextIcon' },
  { id: 'events', name: 'Events', icon: 'CalendarIcon' },
  { id: 'resources', name: 'Resources', icon: 'BookOpenIcon' },
  { id: 'media', name: 'Media', icon: 'PhotoIcon' },
  { id: 'support', name: 'Support', icon: 'HeartIcon' },
];

// Popular searches
export const popularSearches = [
  'Basketball schedule',
  'Athletic handbook',
  'Registration forms',
  'Game results',
  'Booster club',
  'Safety protocols',
  'Facilities',
  'Coach contacts'
];

// Quick navigation items
export const quickNavigation = [
  { name: 'Sports Schedules', url: '/sports', icon: 'CalendarIcon', description: 'View all sports schedules' },
  { name: 'Registration', url: '/family-resources/fees', icon: 'DocumentTextIcon', description: 'Athletic registration and forms' },
  { name: 'Game Results', url: '/sports', icon: 'TrophyIcon', description: 'Latest game results' },
  { name: 'Athletic Handbook', url: '/family-resources/handbook', icon: 'BookOpenIcon', description: 'Complete athletic handbook' },
  { name: 'Contact Info', url: '/contact', icon: 'PhoneIcon', description: 'Contact athletic department' },
  { name: 'Donations', url: '/donations', icon: 'HeartIcon', description: 'Support our programs' },
];

class SearchService {
  // Get all searchable content
  getAllContent() {
    const allContent = [];
    Object.values(mockSearchData).forEach(category => {
      allContent.push(...category);
    });
    return allContent;
  }

  // Perform search with query and filters
  async search(query, filters = {}) {
    try {
      const { category = 'all', limit = 20 } = filters;
      let results = this.getAllContent();

      // Filter by category if specified
      if (category && category !== 'all') {
        if (category === 'resources') {
          results = results.filter(item => item.category === 'Resources');
        } else if (category === 'support') {
          results = results.filter(item => item.category === 'Support');
        } else if (category === 'media') {
          results = results.filter(item => item.category === 'Media');
        } else {
          results = results.filter(item => item.type === category);
        }
      }

      // Search by query if provided
      if (query && query.trim()) {
        const searchTerm = query.toLowerCase().trim();
        results = results.filter(item => {
          return (
            item.title.toLowerCase().includes(searchTerm) ||
            item.description.toLowerCase().includes(searchTerm) ||
            item.category.toLowerCase().includes(searchTerm)
          );
        });

        // Sort by relevance (title matches first, then description)
        results.sort((a, b) => {
          const aTitle = a.title.toLowerCase().includes(searchTerm);
          const bTitle = b.title.toLowerCase().includes(searchTerm);
          
          if (aTitle && !bTitle) return -1;
          if (!aTitle && bTitle) return 1;
          
          return a.title.localeCompare(b.title);
        });
      }

      // Apply limit
      results = results.slice(0, limit);

      return {
        results,
        total: results.length,
        query,
        filters
      };
    } catch (error) {

      throw error;
    }
  }

  // Get search suggestions based on partial query
  async getSuggestions(query) {
    try {
      if (!query || query.length < 2) {
        return popularSearches.slice(0, 5);
      }

      const searchTerm = query.toLowerCase();
      const allContent = this.getAllContent();
      
      const suggestions = allContent
        .filter(item => 
          item.title.toLowerCase().includes(searchTerm) ||
          item.description.toLowerCase().includes(searchTerm)
        )
        .map(item => item.title)
        .slice(0, 8);

      // Add popular searches that match
      const popularMatches = popularSearches
        .filter(search => search.toLowerCase().includes(searchTerm))
        .slice(0, 3);

      return [...new Set([...suggestions, ...popularMatches])];
    } catch (error) {

      return [];
    }
  }

  // Get recent searches from localStorage
  getRecentSearches() {
    try {
      const recent = localStorage.getItem('recentSearches');
      return recent ? JSON.parse(recent) : [];
    } catch (error) {

      return [];
    }
  }

  // Save search to recent searches
  saveRecentSearch(query) {
    try {
      if (!query || query.trim().length < 2) return;
      
      const recent = this.getRecentSearches();
      const updatedRecent = [query, ...recent.filter(q => q !== query)].slice(0, 10);
      
      localStorage.setItem('recentSearches', JSON.stringify(updatedRecent));
    } catch (error) {

    }
  }

  // Clear recent searches
  clearRecentSearches() {
    try {
      localStorage.removeItem('recentSearches');
    } catch (error) {

    }
  }

  // Get filtered results by type
  async getResultsByType(type, limit = 10) {
    try {
      const results = mockSearchData[type] || [];
      return results.slice(0, limit);
    } catch (error) {

      return [];
    }
  }

  // Format search result for display
  formatSearchResult(result) {
    return {
      ...result,
      formattedDate: result.date ? new Date(result.date).toLocaleDateString() : null,
      typeLabel: this.getTypeLabel(result.type),
      categoryColor: this.getCategoryColor(result.category)
    };
  }

  // Get type label for display
  getTypeLabel(type) {
    const labels = {
      sport: 'Sport',
      news: 'News Article',
      event: 'Event',
      page: 'Page'
    };
    return labels[type] || 'Content';
  }

  // Get category color for display
  getCategoryColor(category) {
    const colors = {
      'Sports': 'bg-blue-100 text-blue-800',
      'News': 'bg-green-100 text-green-800',
      'Events': 'bg-purple-100 text-purple-800',
      'Resources': 'bg-orange-100 text-orange-800',
      'Support': 'bg-red-100 text-red-800',
      'Media': 'bg-pink-100 text-pink-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  }
}

export default new SearchService();